import { Notification } from '../api/notifications';

/**
 * Generate a deep link URL for a notification based on its entity type and ID
 * @param notification - The notification object
 * @returns A URL string for navigation
 */
export const generateNotificationLink = (notification: Notification): string => {
  // If the notification already has a direct link, use it
  if (notification.link) {
    return notification.link;
  }

  const { entity_type, entity_id } = notification;
  
  // Extract project ID from the entity_id or message if available
  const projectId = extractProjectIdFromNotification(notification);
  
  if (!projectId) {
    // Fallback to projects page if no project ID can be determined
    return '/projects';
  }

  // Generate deep links based on entity type
  switch (entity_type.toLowerCase()) {
    case 'project_item':
    case 'project_plan':
    case 'task':
      // Link to Project Plan tab with the specific item
      return `/projects/${projectId}?tab=project-plan-2&item=${entity_id}`;
    
    case 'action_item':
    case 'actionitem':
      // Link to Action Items tab with the specific item
      return `/projects/${projectId}?tab=action-items&item=${entity_id}`;
    
    case 'daily_update':
    case 'dailyupdate':
      // Link to Daily Updates tab with the specific item
      return `/projects/${projectId}?tab=daily-updates&item=${entity_id}`;
    
    case 'meeting_note':
    case 'meetingnote':
      // Link to Meeting Notes tab with the specific item
      return `/projects/${projectId}?tab=meeting-notes&item=${entity_id}`;
    
    case 'project':
      // Link to the project overview
      return `/projects/${entity_id}`;
    
    case 'team':
    case 'user':
      // Link to the project team tab
      return `/projects/${projectId}?tab=team`;
    
    case 'location':
    case 'room':
      // Link to the project locations tab
      return `/projects/${projectId}?tab=locations`;
    
    case 'document':
      // Link to the project documents tab
      return `/projects/${projectId}?tab=documents`;
    
    case 'warranty':
      // Link to the project warranties tab
      return `/projects/${projectId}?tab=warranties`;
    
    case 'material':
    case 'material_tracker':
      // Link to the project material tracker tab
      return `/projects/${projectId}?tab=material-tracker`;
    
    default:
      // For unknown entity types, try to link to the project
      return `/projects/${projectId}`;
  }
};

/**
 * Extract project ID from notification
 * @param notification - The notification object
 * @returns Project ID string or null
 */
const extractProjectIdFromNotification = (notification: Notification): string | null => {
  // Try to extract project ID from the message using common patterns
  const message = notification.message.toLowerCase();
  
  // Look for patterns like "project <id>" or "in project <id>"
  const projectIdMatch = message.match(/(?:project|in project)\s+([a-f0-9-]{36})/i);
  if (projectIdMatch) {
    return projectIdMatch[1];
  }
  
  // Look for patterns in the entity_id if it contains project information
  // This might be a compound ID like "project_id:item_id"
  if (notification.entity_id.includes(':')) {
    const parts = notification.entity_id.split(':');
    if (parts.length >= 2) {
      return parts[0];
    }
  }
  
  // Try to extract from user_id if it's project-related
  // This is a fallback - you might need to adjust based on your actual data structure
  
  return null;
};

/**
 * Get the tab name for a given entity type
 * @param entityType - The entity type from the notification
 * @returns Tab name string
 */
export const getTabForEntityType = (entityType: string): string => {
  switch (entityType.toLowerCase()) {
    case 'project_item':
    case 'project_plan':
    case 'task':
      return 'project-plan-2';
    
    case 'action_item':
    case 'actionitem':
      return 'action-items';
    
    case 'daily_update':
    case 'dailyupdate':
      return 'daily-updates';
    
    case 'meeting_note':
    case 'meetingnote':
      return 'meeting-notes';
    
    case 'team':
    case 'user':
      return 'team';
    
    case 'location':
    case 'room':
      return 'locations';
    
    case 'document':
      return 'documents';
    
    case 'warranty':
      return 'warranties';
    
    case 'material':
    case 'material_tracker':
      return 'assets'; // Redirect to assets since material-tracker is hidden
    
    default:
      return 'project-plan-2'; // Default to project plan
  }
}; 